// R115 guest — the PMS send confirm step, lifted out of QuoInboxPage so the
// Calendar's Message action posts to a guest's Airbnb / VRBO / Booking thread
// behind EXACTLY the same confirm (channel + guest + property + stay + a
// preview; Cancel focused so Enter never sends by accident; a click outside or
// Cancel closes it unless a send is in flight; "Sending…" inside the modal).
//
// The overlay mounts on document.body — opening, cancelling and its busy
// state never redraw the host page (QuoInboxPage keeps its thread scroll and
// draft caret, R112 review [15]). It closes itself when the host says it is
// gone (isAlive() false on a mutation of the watched element), and the host
// may close it any time. Markup keeps QuoInboxPage's classes (.qx-ovl /
// .qx-modal / .qx-kv / .qx-preview / .qx-modal-btns) so that page's styles
// still apply; a host without them (the Calendar) gets the same look from the
// scoped `.pmsc-ovl` rules injected once on first open. z-index is the host's
// call: QuoInboxPage keeps its 9500, the Calendar uses 350 (above its form
// sheet 300 and the ⋯ kebab 340, below toasts 400 — CALENDAR_CONTRACT z-order).
import { Formatters } from '../lib/formatters';

export interface PmsConfirmInfo {
  /** e.g. "Airbnb" — the thread's channel label */
  channel: string;
  guest: string;
  property: string;
  /** "Oct 15 → Oct 18" (optional) */
  stay?: string;
  body: string;
}
export interface PmsConfirmOpts {
  onSend: () => void | Promise<void>;
  onCancel?: () => void;
  /** closes the modal when it reports false on a mutation of `watch` (childList) */
  isAlive?: () => boolean;
  watch?: HTMLElement | null;
  zIndex?: number;
}

const esc = (s: unknown) => Formatters.escapeHtml(s == null ? '' : String(s));
const CSS_ID = 'pmsc-css';
const CSS = `
.pmsc-ovl { position:fixed; inset:0; background:rgba(15,23,42,.45); display:flex; align-items:center; justify-content:center; padding:16px; }
.pmsc-ovl .qx-modal { background:var(--card); border-radius:var(--radius); box-shadow:var(--shadow-pop); width:min(520px, 100%); padding:20px 22px; box-sizing:border-box; }
.pmsc-ovl .qx-modal h3 { margin:0 0 6px; font-size:16px; font-weight:700; color:var(--text); }
.pmsc-ovl .qx-modal p { margin:0 0 12px; font-size:12.5px; color:var(--text-muted); line-height:1.5; }
.pmsc-ovl .qx-kv { display:flex; justify-content:space-between; gap:10px; font-size:12.5px; padding:4px 0; }
.pmsc-ovl .qx-kv span:first-child { color:var(--text-muted); }
.pmsc-ovl .qx-kv span:last-child { color:var(--text); font-weight:500; text-align:right; min-width:0; overflow:hidden; text-overflow:ellipsis; }
.pmsc-ovl .qx-preview { border:1px solid var(--border); background:var(--bg-subtle); border-radius:var(--radius-sm); padding:10px 12px; font-size:13px; margin:10px 0 12px; white-space:pre-wrap; word-break:break-word; }
.pmsc-ovl .qx-modal-btns { display:flex; justify-content:flex-end; gap:8px; }
.pmsc-ovl .qx-modal-btns .btn { min-height:36px; }
`;
function ensureCss(): void {
  if (document.getElementById(CSS_ID)) return;
  const st = document.createElement('style');
  st.id = CSS_ID; st.textContent = CSS;
  document.head.appendChild(st);
}

/** The modal's markup (QuoInboxPage's R112 confirm, verbatim). */
export function pmsConfirmHtml(info: PmsConfirmInfo, sending = false): string {
  const content = String(info.body || '').trim();
  const preview = content.length > 120 ? `${content.slice(0, 120)}…` : content;
  return `
        <div class="qx-modal" role="dialog" aria-modal="true" aria-labelledby="qx-confirm-h">
          <h3 id="qx-confirm-h">Send this to the guest on the PMS thread?</h3>
          <p>It posts to the guest's ${esc(info.channel)} conversation through Hostaway right away and can't be unsent. Airbnb may reject phone numbers or links before the booking is confirmed.</p>
          <div class="qx-kv"><span>Channel</span><span>PMS · ${esc(info.channel)} (via Hostaway)</span></div>
          <div class="qx-kv"><span>Guest</span><span>${esc(info.guest)}</span></div>
          <div class="qx-kv"><span>Property</span><span>${esc(info.property || '—')}</span></div>
          ${info.stay ? `<div class="qx-kv"><span>Stay</span><span>${esc(info.stay)}</span></div>` : ''}
          <div class="qx-preview">${esc(preview)}</div>
          <div class="qx-modal-btns">
            <button type="button" class="btn btn-secondary" data-qx-confirm-cancel>Cancel</button>
            <button type="button" class="btn btn-primary" data-qx-confirm-send ${sending ? 'disabled' : ''}>${sending ? 'Sending…' : 'Send to guest ▸'}</button>
          </div>
        </div>`;
}

export class PmsSendConfirm {
  private el: HTMLElement | null = null;
  private mo: MutationObserver | null = null;
  private busy = false;

  get isOpen(): boolean { return !!this.el; }

  open(info: PmsConfirmInfo, opts: PmsConfirmOpts): void {
    this.close();
    ensureCss();
    const box = document.createElement('div');
    box.className = 'qx-ovl pmsc-ovl';
    box.style.zIndex = String(opts.zIndex != null ? opts.zIndex : 9500);
    box.innerHTML = pmsConfirmHtml(info, false);
    document.body.appendChild(box);
    this.el = box; this.busy = false;
    const cancel = () => { if (this.busy) return; this.close(); if (opts.onCancel) opts.onCancel(); };
    box.querySelector('[data-qx-confirm-cancel]')?.addEventListener('click', cancel);
    box.querySelector('[data-qx-confirm-send]')?.addEventListener('click', () => { if (!this.busy) void opts.onSend(); });
    box.addEventListener('click', (e) => { if (e.target === box) cancel(); });
    box.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); cancel(); } });
    if (opts.watch && opts.isAlive) {
      try {
        const mo = new MutationObserver(() => { if (!opts.isAlive!()) this.close(); });
        mo.observe(opts.watch, { childList: true });
        this.mo = mo;
      } catch { /* no observer — the host closes it itself */ }
    }
    box.querySelector<HTMLElement>('[data-qx-confirm-cancel]')?.focus();   // Enter never sends by accident
  }

  setBusy(busy: boolean): void {
    this.busy = busy;
    const box = this.el;
    if (!box) return;
    const send = box.querySelector<HTMLButtonElement>('[data-qx-confirm-send]');
    const cancel = box.querySelector<HTMLButtonElement>('[data-qx-confirm-cancel]');
    if (send) { send.disabled = busy; send.textContent = busy ? 'Sending…' : 'Send to guest ▸'; }
    if (cancel) cancel.disabled = busy;
  }

  close(): void {
    if (this.mo) { this.mo.disconnect(); this.mo = null; }
    if (this.el) { this.el.remove(); this.el = null; }
    this.busy = false;
  }
}
